import { ExternalLink, Globe, Link, Send, Target, TrendingUp, Zap } from 'lucide-react'
import React from 'react'

interface InternalLink {
  url: string
  anchorText: string
  context?: string
  priority?: 'high' | 'medium' | 'low'
  targetSection?: string
  isManual?: boolean
}

interface InternalLinksData {
  linksAdded: InternalLink[]
  totalUrlsAnalyzed?: number
  sitemapMethod?: 'sitemap' | 'internal_crawl' | 'both'
  shopifyUrlsIncluded?: number
  reasoning?: string
}

interface InternalLinksDisplayProps {
  internalLinks: InternalLinksData
  onPublishLinks?: (description: string) => void
  articleUrl: string
}

const PriorityBadge = ({ priority }: { priority?: 'high' | 'medium' | 'low' }) => {
  const config = {
    high: {
      label: 'High',
      icon: <TrendingUp className="h-3 w-3" />,
      bg: 'bg-red-50',
      text: 'text-red-700',
      border: 'border-red-200',
    },
    medium: {
      label: 'Medium',
      icon: <Zap className="h-3 w-3" />,
      bg: 'bg-amber-50',
      text: 'text-amber-700',
      border: 'border-amber-200',
    },
    low: {
      label: 'Low',
      icon: <Target className="h-3 w-3" />,
      bg: 'bg-blue-50',
      text: 'text-blue-700',
      border: 'border-blue-200',
    },
  }

  const style = config[priority || 'medium']

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-xs font-medium ${style.bg} ${style.text} ${style.border}`}
    >
      {style.icon}
      {style.label}
    </span>
  )
}

const LinkCard = ({
  link,
  index,
  onPublish,
  articleUrl,
}: {
  link: InternalLink
  index: number
  onPublish?: (description: string) => void
  articleUrl: string
}) => {
  const handleLinkClick = (e: React.MouseEvent) => {
    // Allow the default link behavior (open in new tab)
    e.stopPropagation()
  }

  const formatIndividualLinkMessage = (link: InternalLink, articleUrl: string): string => {
    return `I want to add this internal link to ${articleUrl}:

• **Link URL**: ${link.url}
• **Anchor Text**: "${link.anchorText}"
• **Priority**: ${link.priority || 'medium'}
${link.context ? `• **Context**: ${link.context}` : ''}
${link.isManual ? '• **Type**: Manual link (user-specified)' : '• **Type**: Auto-discovered link'}

Use shopifyWebPublisher with:
- action: 'update'
- targetSlug: extract from URL
- changes: [{
    changeType: 'insert_after',
    findText: 'relevant content section',
    replaceWith: '[${link.anchorText}](${link.url})',
    changeReason: 'Add internal link for improved SEO and user navigation'
  }]

Apply only this specific link addition, not other changes.`
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md">
      <div className="mb-3 flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <Link className="h-4 w-4 text-blue-600" />
          <span className="text-sm font-medium text-gray-500">Link {index + 1}</span>
          <PriorityBadge priority={link.priority} />
          {link.isManual && (
            <span className="rounded-full border border-purple-200 bg-purple-50 px-2 py-1 text-xs font-medium text-purple-700">
              Manual
            </span>
          )}
        </div>
        {onPublish && (
          <button
            onClick={() => onPublish(formatIndividualLinkMessage(link, articleUrl))}
            className="inline-flex items-center gap-1 rounded-md border border-blue-200 bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 transition-colors hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
          >
            <Send className="h-3 w-3" />
            Publish this link
          </button>
        )}
      </div>

      <div className="space-y-3">
        {/* Main link display */}
        <div className="rounded-lg border border-blue-100 bg-blue-50 p-3">
          <div className="flex items-center gap-2">
            <a
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleLinkClick}
              className="flex items-center gap-2 text-blue-700 hover:text-blue-900 hover:underline"
            >
              <span className="font-medium">{link.anchorText}</span>
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>
          <div className="mt-1 break-all text-xs text-blue-600">{link.url}</div>
        </div>

        {/* Context or target section */}
        {(link.context || link.targetSection) && (
          <div className="space-y-2">
            {link.context && (
              <div className="text-sm text-gray-600">
                <span className="font-medium">Context:</span> {link.context}
              </div>
            )}
            {link.targetSection && (
              <div className="text-sm text-gray-600">
                <span className="font-medium">Target section:</span> {link.targetSection}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

const StatCard = ({
  icon,
  label,
  value,
  description,
}: {
  icon: React.ReactNode
  label: string
  value: string | number
  description?: string
}) => (
  <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
    <div className="flex items-center gap-3">
      <div className="rounded-lg bg-indigo-100 p-2">{icon}</div>
      <div className="flex-1">
        <div className="text-lg font-semibold text-gray-900">{value}</div>
        <div className="text-sm font-medium text-gray-600">{label}</div>
        {description && <div className="text-xs text-gray-500">{description}</div>}
      </div>
    </div>
  </div>
)

export function InternalLinksDisplay({
  internalLinks,
  onPublishLinks,
  articleUrl,
}: InternalLinksDisplayProps) {
  if (!internalLinks || !internalLinks.linksAdded || internalLinks.linksAdded.length === 0) {
    return null
  }

  const formatAllLinksMessage = (internalLinks: InternalLinksData, articleUrl: string): string => {
    const linksList = internalLinks.linksAdded
      .map(
        (link, i) =>
          `${i + 1}. [${link.anchorText}](${link.url})${link.isManual ? ' *(manual)*' : ''}`
      )
      .join('\n')

    return `I want to add all ${internalLinks.linksAdded.length} internal links to ${articleUrl}:

${linksList}

**Link Discovery Summary:**
- Total URLs analyzed: ${internalLinks.totalUrlsAnalyzed || 'Unknown'}
- Discovery method: ${internalLinks.sitemapMethod || 'sitemap'}
${internalLinks.shopifyUrlsIncluded ? `- Shopify URLs included: ${internalLinks.shopifyUrlsIncluded}` : ''}
${internalLinks.reasoning ? `- Strategy: ${internalLinks.reasoning}` : ''}

Use shopifyWebPublisher with:
- action: 'update'
- targetSlug: extract from URL
- internalLinks: ${JSON.stringify(internalLinks.linksAdded)}

Apply all these internal links to improve SEO and user navigation.`
  }

  const highPriorityCount = internalLinks.linksAdded.filter(
    (link) => link.priority === 'high'
  ).length
  const manualCount = internalLinks.linksAdded.filter((link) => link.isManual).length

  return (
    <div className="space-y-6">
      {/* Header with publish all button */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-800">🔗 Internal Links Added</h2>
          <div className="text-sm text-gray-500">
            Added {internalLinks.linksAdded.length} internal link
            {internalLinks.linksAdded.length !== 1 ? 's' : ''} to improve SEO and user navigation
          </div>
        </div>
        {onPublishLinks && (
          <button
            onClick={() => onPublishLinks(formatAllLinksMessage(internalLinks, articleUrl))}
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-all duration-200 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1"
          >
            <Send className="h-4 w-4" />
            Publish all links
          </button>
        )}
      </div>

      {/* Statistics cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={<Link className="h-5 w-5 text-indigo-600" />}
          label="Links Added"
          value={internalLinks.linksAdded.length}
          description="Total internal links"
        />
        <StatCard
          icon={<TrendingUp className="h-5 w-5 text-red-600" />}
          label="High Priority"
          value={highPriorityCount}
          description="Priority links"
        />
        <StatCard
          icon={<Target className="h-5 w-5 text-purple-600" />}
          label="Manual Links"
          value={manualCount}
          description="User-specified"
        />
        <StatCard
          icon={<Globe className="h-5 w-5 text-green-600" />}
          label="URLs Analyzed"
          value={internalLinks.totalUrlsAnalyzed || '—'}
          description={internalLinks.sitemapMethod || 'Discovery method'}
        />
      </div>

      {/* Strategy reasoning */}
      {internalLinks.reasoning && (
        <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-4">
          <div className="flex items-start gap-3">
            <div className="rounded-full bg-indigo-100 p-1">
              <Target className="h-4 w-4 text-indigo-600" />
            </div>
            <div>
              <div className="text-sm font-medium text-indigo-900">Linking Strategy</div>
              <div className="text-sm text-indigo-800">{internalLinks.reasoning}</div>
            </div>
          </div>
        </div>
      )}

      {/* Individual link cards */}
      <div className="space-y-4">
        <div className="text-lg font-medium text-gray-800">Added Links</div>
        <div className="grid gap-4">
          {internalLinks.linksAdded.map((link, index) => (
            <LinkCard
              key={index}
              link={link}
              index={index}
              onPublish={onPublishLinks}
              articleUrl={articleUrl}
            />
          ))}
        </div>
      </div>
    </div>
  )
}
